import React, { useState } from "react";
import { FaEye } from "react-icons/fa";
import { MdEditSquare, MdDelete, MdRestore } from "react-icons/md"; // ✅ Restore icon

const formatDateTime = (value) => {
  if (!value) {
    return { date: "No update data", time: "" };
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return { date: "Invalid date", time: "" };
  }

  return {
    date: date.toLocaleDateString(),
    time: date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
  };
};

const DataTable = ({
  locations,
  editClick,
  viewClick,
  deleteConfirmation,
  restoreClick,
  showVideoColumn,
  showViewPage,
  showCategoery,
  showRestore,
  showStatus,
  showCreatedBy,
  highlightRecent = false,
  recentHours = 24
}) => {
  if (!locations || locations.length === 0) {
    return <p className="tableEmptyState">No locations available</p>;
  }

  const getRecentState = (loc) => {
    if (!highlightRecent) return null;
    const createdAt = loc?.createdAt ? new Date(loc.createdAt).getTime() : null;
    const updatedAt = loc?.updatedAt ? new Date(loc.updatedAt).getTime() : null;
    const threshold = Date.now() - recentHours * 60 * 60 * 1000;
    if (!createdAt && !updatedAt) return null;

    const createdRecent = createdAt && createdAt >= threshold;
    const updatedRecent = updatedAt && updatedAt >= threshold;
    const isLikelyNew =
      createdRecent && (!updatedAt || Math.abs(updatedAt - createdAt) < 5 * 60 * 1000);

    if (isLikelyNew) return "new";
    if (updatedRecent) return "updated";
    return null;
  };

  return (
    <div className="tableWrapper">
      <div className="tableContainer">
        <table>
          <thead>
            <tr>
              <th className="srNo">Sr. no</th>
              <th>Name</th>
              {showCategoery && <th>Category</th>}
              <th>Type</th>
              {showStatus && <th>Status</th>}
              {showCreatedBy && <th>Created By</th>}
              <th>Last Modified</th>
              {showVideoColumn && <th>Watch video</th>}
              {showViewPage && <th>View</th>}
              <th>Edit</th>
              {showRestore && <th>Restore</th>}
              <th>Delete</th>
            </tr>
          </thead>
          <tbody>
            {locations.map((loc, index) => (
              <tr key={loc._id || loc.id || index}>
                {(() => {
                  const recentState = getRecentState(loc);
                  const timestamp = formatDateTime(loc.updatedAt);
                  return (
                    <>
                      <td>{index + 1}</td>
                      <td>
                        <div className="tablePrimaryCell">
                          <strong>{loc.locationName || loc.title || loc.location || "Unknown Name"}</strong>
                          <span className="tableMetaText">
                            {recentState ? (
                              <span className={recentState ? `recent-${recentState}` : ""}>
                                {recentState === "new" ? "Newly created" : "Recently updated"}
                              </span>
                            ) : (
                              loc.category || loc.type || "Location"
                            )}
                          </span>
                        </div>
                      </td>
                      {showCategoery && <td>{loc.category || "Unknown Category"}</td>}
                      <td>{loc.type || loc.category || "Unknown Type"}</td>
                      {showStatus && (
                        <td className="tableStatusCell">
                          <span className={`status-pill status-${(loc.approvalStatus || "unknown").toLowerCase()}`}>
                            {formatStatus(loc.approvalStatus)}
                          </span>
                        </td>
                      )}
                      {showCreatedBy && (
                        <td>{loc.createdByLabel || loc.createdById || "—"}</td>
                      )}
                      <td>
                        <div className="tableTimestamp">
                          <strong>{timestamp.date}</strong>
                          {timestamp.time ? <span>{timestamp.time}</span> : null}
                        </div>
                      </td>

                      {showVideoColumn && (
                        <td>
                          <button
                            type="button"
                            className="tableActionBtn tableActionBtn--view"
                            onClick={() => viewClick(loc.videoLink)}
                          >
                            <FaEye />
                            <span>Watch</span>
                          </button>
                        </td>
                      )}

                      {showViewPage && (
                        <td>
                          <button
                            type="button"
                            className="tableActionBtn tableActionBtn--view"
                            onClick={() => viewClick?.(loc)}
                          >
                            <FaEye />
                            <span>View</span>
                          </button>
                        </td>
                      )}
                      <td>
                        <button type="button" className="tableActionBtn tableActionBtn--edit" onClick={() => editClick(loc)}>
                          <MdEditSquare />
                          <span>Edit</span>
                        </button>
                      </td>

                      {showRestore && (
                        <td>
                          <button
                            type="button"
                            className="tableActionBtn tableActionBtn--restore"
                            onClick={() => restoreClick(loc._id, loc.segment)}
                          >
                            <MdRestore />
                            <span>Restore</span>
                          </button>
                        </td>
                      )}

                      <td>
                        <button
                          type="button"
                          className="tableActionBtn tableActionBtn--delete"
                          onClick={() => deleteConfirmation(loc._id)}
                        >
                          <MdDelete />
                          <span>{showRestore ? "Delete" : "Remove"}</span>
                        </button>
                      </td>
                    </>
                  );
                })()}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const formatStatus = (status) => {
  const normalized = (status || "").toLowerCase();
  if (normalized === "approved") return "Approved";
  if (normalized === "rejected") return "Rejected";
  if (normalized === "pending_dm") return "Pending (District Manager)";
  if (normalized === "pending_superadmin") return "Pending (Super Admin)";
  if (normalized === "draft") return "Draft";
  return status || "Unknown";
};

const SubAdminTable = ({ subAdmins, deleteConfirmation, viewProfile, renderEditorsSubTable }) => {
  const [expandedRows, setExpandedRows] = useState({});

  const toggleEditors = (id) => {
    setExpandedRows((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  if (!subAdmins || subAdmins.length === 0) {
    return <p>No Sub-Admins available</p>;
  }

  return (
    <div className="tableWrapper">
      <div className="tableContainer">
        <table border="1">
          <thead>
            <tr>
              <th className="srNo">Sr. No</th>
              <th>Name</th>
              <th>Contact Number</th>
              <th>Location</th>
              <th>Segments</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {subAdmins.map((admin, index) => (
              <React.Fragment key={admin.id}>
                <tr>
                  <td>{index + 1}</td>
                  <td>{admin.name || "Unknown Name"}</td>
                  <td>{admin.contactNumber || "N/A"}</td>
                  <td>{admin.location || admin.district || "N/A"}</td>
                  <td style={{ maxWidth: 240 }}>
                    {admin.assignedSections?.length
                      ? admin.assignedSections.join(", ")
                      : "No Sections Assigned"}
                  </td>
                  <td style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                  <button
                    className="btn-blue"
                    style={{ padding: "6px 10px", borderRadius: "8px" }}
                    onClick={() => viewProfile?.(admin)}
                  >
                    View Profile
                  </button>
                  {renderEditorsSubTable && (
                    <button
                      className="btn-white"
                      style={{ padding: "6px 10px", borderRadius: "8px" }}
                      onClick={() => toggleEditors(admin.id)}
                    >
                      {expandedRows[admin.id] ? "Hide Editors" : "Show Editors"}
                    </button>
                  )}
                  <MdDelete
                    className="icon-24"
                    title="Delete"
                    onClick={() => deleteConfirmation(admin.id)}
                    style={{ cursor: "pointer", color: "red" }}
                  />
                </td>
              </tr>
              {renderEditorsSubTable && expandedRows[admin.id] && (
                <tr>
                  <td colSpan={6} style={{ background: "#f8fafc" }}>
                    <div style={{ padding: "8px 12px" }}>
                      <div style={{ fontWeight: 700, color: "#0f172a", marginBottom: "6px" }}>
                        Editors Created: {admin.editorsCount ?? 0}
                        </div>
                        {renderEditorsSubTable(admin.editors || [])}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export { DataTable, SubAdminTable };
